import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    MenuItem,
    TextField
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import helpers from "../../helpers/common";
import {useEffect, useRef, useState} from "react";
import useRootHook from "../../hooks/useRoot";
import * as React from "react";
import {ContentCopy} from "@mui/icons-material";
import Container from "@mui/material/Container";
import paymentDomains from "../../models/payment-domains";
import merchants from "../../models/merchants";

const ENV_PAYMENT_LINK_HOST = process.env.REACT_APP_PAYMENT_LINK_HOST || "paywithmypage.com";

const customerUuid32 = (uuid) => (uuid || '').replace(/-/g, '');

export default function GetLinkDialog(props) {
    const { setDialogIsOpened, dialogIsOpened, setSelectedUser, selectedUser} = props
    const {notify, defaultCurrency} = useRootHook();
    const amountRef = useRef(null)
    const [currencyData, setCurrencyData] = useState([])
    const [selectedCurrency, setSelectedCurrency] = useState('')
    const [paymentHost, setPaymentHost] = useState(null)
    const [remainingDaily, setRemainingDaily] = useState(null)

    useEffect(() => {
        if (!dialogIsOpened || !selectedUser?.uuid) {
            setCurrencyData([])
            setSelectedCurrency('')
            return
        }
        merchants.getAllowedCurrencies(customerUuid32(selectedUser.uuid))
            .then((data) => {
                const list = Array.isArray(data) ? data : []
                setCurrencyData(list)
                const defaultCode = String(defaultCurrency?.currency || '').toUpperCase()
                const hasDefault = list.some((c) => String(c.currency || '').toUpperCase() === defaultCode)
                setSelectedCurrency(hasDefault ? defaultCode : (list[0]?.currency || ''))
            })
            .catch(() => {
                setCurrencyData([])
                setSelectedCurrency('')
            })
    }, [dialogIsOpened, selectedUser?.uuid, defaultCurrency?.currency])

    useEffect(() => {
        if (!dialogIsOpened) return
        paymentDomains.getActive()
            .then((data) => {
                const host = data?.host ? String(data.host).trim() : null
                setPaymentHost(host || null)
            })
            .catch(() => setPaymentHost(null))
    }, [dialogIsOpened])

    useEffect(() => {
        if (!dialogIsOpened || !selectedUser?.uuid) {
            setRemainingDaily(null)
            return
        }
        merchants.getRemainingDaily(customerUuid32(selectedUser.uuid))
            .then((data) => {
                setRemainingDaily(data?.remaining != null ? data : null)
            })
            .catch(() => setRemainingDaily(null))
    }, [dialogIsOpened, selectedUser?.uuid])

    const handleClose = () => {
        setDialogIsOpened(false)
        setSelectedUser(null)
    }

    const validate = (amount, currency) => {
        if (!amount) {
            return [false, 'Please insert amount']
        }
        if (!currency) {
            return [false, 'Please select currency']
        }
        return [true]
    }

    const handleCopy = async () => {
        const amount = amountRef.current.value
        const currency = selectedCurrency

        const [validated, message] = validate(amount, currency)

        if (!validated) {
            notify(message, 3000)
            return
        }

        const currencyRecord = currencyData.find((data) => data.currency === currency.toUpperCase())
        const subunitAmount = parseInt(parseFloat(amount) * currencyRecord.subunit, 10)

        try {
            const check = await merchants.validateLinkAmount(customerUuid32(selectedUser.uuid), subunitAmount, currency)
            if (check && check.allowed === false) {
                notify(check.message || 'Amount exceeds your remaining daily limit.', 5000)
                return
            }
        } catch (e) {
            notify(e?.message || 'Could not validate limit.', 3000)
            return
        }

        const linkQueryData = new URLSearchParams({
            c: helpers.UUIDv4Transform(selectedUser.uuid),
            a: subunitAmount,
            cr: currency
        }).toString()

        const host = paymentHost || ENV_PAYMENT_LINK_HOST
        const link = `https://${host}/payment?${linkQueryData}`

        helpers.copyTextToClipboard(link)
            .then(() => {
                notify('Link copied to Clipboard', 2000)
                handleClose()
            })
    }

    if (!currencyData) return null

    return <Dialog onClose={handleClose} open={dialogIsOpened}>
        <Container component="form" onSubmit={ev => ev.preventDefault()}>
            <DialogTitle>Create Static Payment Link</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Please select amount and currency to create a static payment link
                </DialogContentText>
                {remainingDaily != null && remainingDaily.remaining != null && (
                    <DialogContentText sx={{ mb: 1 }}>
                        Remaining today: {Number(remainingDaily.remaining).toFixed(2)} {remainingDaily.default_currency || ''}
                    </DialogContentText>
                )}
                <TextField
                    inputRef={amountRef}
                    required={true}
                    autoFocus
                    margin="dense"
                    id="amount"
                    label="Amount"
                    type="number"
                    fullWidth

                />
                <TextField
                    select // tell TextField to render select
                    label="Currency"
                    id="currency"
                    name="currency"
                    fullWidth
                    value={selectedCurrency}
                    onChange={(ev) => setSelectedCurrency(ev.target.value)}
                >
                    {
                        currencyData.map((currencyRecord) => {
                            return <MenuItem key={`id-${currencyRecord.id}`} data-subunit={currencyRecord.subunit} value={currencyRecord.currency}>{currencyRecord.currency}</MenuItem>
                        })
                    }
                </TextField>
            </DialogContent>
            <DialogActions>
                <Button type="submit" onClick={handleCopy} variant="contained" startIcon={<ContentCopy />}>
                    Copy Link
                </Button>
            </DialogActions>
        </Container>
    </Dialog>
}